"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { ExternalLink, Gem, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CATALOGUE_STATS, JOUDI_CATEGORIES, JOUDI_REFS, type RefJoudi } from "@/lib/catalogue-data";

/* ============================================================
   PARFUMERIE JOUDI — LES 33 ÉDITIONS ROYALES
   Rangée en bas de l'audience royale, selon la volonté de
   Son Altesse : la collection JOUDI « déjà générée » rejoint
   le catalogue de la Maison DIW'LINE.
   ============================================================ */

export function JoudiCollection() {
  const [cat, setCat] = useState("*");
  const [sel, setSel] = useState<RefJoudi | null>(null);

  const liste = useMemo(
    () => JOUDI_REFS.filter(j => cat === "*" || j.categorie === cat),
    [cat],
  );

  return (
    <section id="joudi" aria-label="Parfumerie JOUDI — les 33 éditions royales" className="relative scroll-mt-20 bg-royal-brown py-20 md:py-28">
      <div className="bg-royal-vignette pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center">
          <p className="font-elegant text-lg italic text-gold">Rangée en bas de l&apos;audience royale — selon la volonté de Son Altesse</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-wide text-gold-gradient animate-shimmer md:text-5xl">
            Parfumerie JOUDI — Les 33 Éditions Royales
          </h2>
          <div className="mx-auto mt-5 flex items-center justify-center gap-3" aria-hidden>
            <span className="h-px w-16 bg-gradient-to-r from-transparent to-gold/70" />
            <Sparkles className="h-4 w-4 text-gold" />
            <span className="h-px w-16 bg-gradient-to-l from-transparent to-gold/70" />
          </div>
          <p className="mx-auto mt-5 max-w-3xl text-[14.5px] leading-relaxed text-cream/70">
            Les {CATALOGUE_STATS.joudi} éditions JOUDI déjà générées rejoignent aujourd&apos;hui le Grand Catalogue de la
            Maison : amphores d&apos;apparat, canettes impériales, flacons PET de cour et séries des cinq continents —
            l&apos;élixir « Raisin de Medine » et ses prouesses de verre, offerts en parfumerie royale à l&apos;Oummah
            et aux connaisseurs du monde entier.
          </p>
        </div>

        {/* Filtres par catégorie */}
        <div className="mt-10 flex flex-wrap justify-center gap-2.5" role="group" aria-label="Filtrer les éditions JOUDI par catégorie">
          {["*", ...JOUDI_CATEGORIES].map(c => {
            const actif = c === cat;
            const label = c === "*" ? "Toutes les éditions" : c;
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                aria-pressed={actif}
                className={`min-h-10 rounded-full border px-4 py-2 text-[11.5px] uppercase tracking-[0.14em] transition-all ${
                  actif
                    ? "border-gold bg-gold text-royal shadow-[0_8px_25px_rgba(212,175,55,0.25)]"
                    : "border-gold/35 bg-royal-panel/70 text-cream/75 hover:border-gold/70 hover:text-gold"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Grille des 33 éditions */}
        <motion.div
          key={cat}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
          role="list"
        >
          {liste.map(j => (
            <button
              key={j.numero}
              role="listitem"
              onClick={() => setSel(j)}
              aria-label={`Ouvrir la fiche de l'édition JOUDI ${j.numero} — ${j.titre}`}
              className="group relative overflow-hidden rounded-sm border border-gold/25 bg-royal-panel text-left transition-all duration-300 hover:-translate-y-1 hover:border-gold/70 hover:shadow-[0_18px_50px_rgba(0,0,0,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              <Image
                src={j.image}
                alt={`Édition JOUDI ${j.numero} — ${j.titre}`}
                width={720}
                height={720}
                loading="lazy"
                className="aspect-square w-full transition-transform duration-500 group-hover:scale-[1.05]"
              />
              <span className="flex items-center justify-between gap-2 border-t border-gold/20 bg-royal/85 px-2.5 py-2">
                <span className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-gold">{j.numero}</span>
                <span className="truncate text-[10.5px] text-cream/70">{j.titre}</span>
              </span>
              <span className="pointer-events-none absolute inset-0 border border-gold/0 transition-colors group-hover:border-gold/50" aria-hidden />
            </button>
          ))}
        </motion.div>

        {/* Boutique officielle */}
        <div className="mx-auto mt-12 max-w-3xl rounded-sm border border-gold/25 bg-royal-panel/70 p-6 text-center">
          <p className="font-elegant text-[15px] italic text-gold/85">
            « La Boutique Impériale JOUDI — 御店 · L&apos;Eau des Rois »
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://boutique.joudi.online"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-gold bg-gold px-5 text-[11.5px] font-semibold uppercase tracking-[0.2em] text-royal transition-all hover:bg-transparent hover:text-gold"
            >
              Boutique Impériale <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
            <a
              href="https://joudi.online"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-gold/50 px-5 text-[11.5px] font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-royal"
            >
              Maison JOUDI <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </div>
        </div>
      </div>

      {/* Fiche édition royale */}
      <Dialog open={!!sel} onOpenChange={o => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-gold/35 bg-royal-brown text-cream scrollbar-royale md:max-w-2xl">
          {sel && (
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="gold-frame overflow-hidden rounded-sm">
                <Image src={sel.image} alt={`Édition JOUDI ${sel.numero} — ${sel.titre}`} width={720} height={720} className="w-full" />
              </div>
              <div>
                <DialogHeader className="text-left">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.3em] text-gold">{sel.numero} — Collection JOUDI</p>
                  <DialogTitle className="font-display text-2xl font-bold leading-snug text-gold-gradient">
                    {sel.titre}
                  </DialogTitle>
                  <DialogDescription className="pt-1 font-elegant text-[15px] italic text-cream/75">
                    {sel.categorie}
                  </DialogDescription>
                </DialogHeader>
                <p className="mt-4 text-[13.5px] leading-relaxed text-cream/75">{sel.description}</p>
                <p className="mt-5 flex items-start gap-2.5 rounded-sm border border-gold/20 bg-royal-panel/80 p-3.5 text-[12px] leading-relaxed text-cream/65">
                  <Gem className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
                  Édition numérotée de la Parfumerie JOUDI — rangée en bas du Grand Catalogue, fidèlement soumise à la
                  recommandation de Son Altesse.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
